/**
 * Which of the two platforms this bundle IS, and which legal entity operates it.
 *
 * TodAi ships as two sites on two origins, not one site with a shop path:
 *
 *   todaidesign.net — the planning platform.   Operator: TodAidesign.
 *   todae.net       — the equipment shop.      Operator: Todae (Pty) Ltd.
 *
 * They are separate registered companies, so this is not branding. The operator
 * named here is the party the visitor contracts with, the party whose company
 * details ECTA s43(1) requires on the page, and the party whose VAT number and
 * bank account belong on the invoice. Getting it from one constant means a page
 * cannot quietly attribute one company's sale to the other.
 *
 * Chosen at BUILD time from VITE_PLATFORM (`npm run build:shop` /
 * `build:planner`), not at runtime from the hostname, because SSG prerenders a
 * fixed route list: a single bundle serving both hosts would have to ship the
 * planner's 784-file designer to the shop and let Google index planner pages on
 * the shop domain. Two builds also give each domain its own robots.txt,
 * sitemap and legal set — which is the separation, expressed in files.
 *
 * The two are joined by the cart handoff in src/lib/todae.ts: a base64url JSON
 * payload in the URL, so it crosses origins without a shared cookie or session.
 */

import { BRAND } from '@/config/brand';

export type Platform = 'shop' | 'planner';

/** Defaults to 'planner' so an un-flagged build (`npm run build`, dev server)
 *  is the full site it has always been — the shop is the deliberate opt-in. */
export const PLATFORM: Platform =
  (import.meta.env.VITE_PLATFORM as Platform | undefined) === 'shop' ? 'shop' : 'planner';

export const isShop = PLATFORM === 'shop';
export const isPlanner = PLATFORM === 'planner';

/**
 * Path prefix the storefront is mounted under, WITH its trailing slash.
 *
 * Empty on todae.net (the shop is the whole site, so its home is `/`), 'todae/'
 * inside the planner build. Route definitions do not need this — a child path
 * is relative to its parent either way — but every `getStaticPaths` returns
 * absolute paths and must build them from here.
 */
export const SHOP_PREFIX = isShop ? '' : 'todae/';

/**
 * Where the storefront lives, as an href.
 *
 * On todae.net the shop IS the site, so its home is `/` and a sub-page is
 * `/cart`. Inside the planner build the same pages hang under `/todae`. Every
 * in-app link to a storefront page must be built from these two rather than
 * written literally, or it 404s on one of the two platforms.
 *
 * SHOP_HOME is separate from `${SHOP_BASE}/` on purpose: SHOP_BASE is empty on
 * the shop, and `<Link to="">` is not a link to the root.
 */
export const SHOP_BASE = isShop ? '' : '/todae';
export const SHOP_HOME = isShop ? '/' : '/todae';

/**
 * Absolute URL of a storefront page — for links, canonicals, OG tags and JSON-LD.
 *
 * ALWAYS on the shop's own origin, from the planner as much as from the shop.
 * Todae and TodAidesign are two companies with two sites on two domains; the
 * join between them is fundamental, but it is a crossing between domains, not
 * a path inside one of them. The planner build still carries the shop pages
 * under /todae because both sites are built from one tree — that mount is a
 * build artefact, and nothing may link a visitor INTO it: "Store" in the
 * planner's header lands on todae.net, and so does the cart handoff.
 *
 * A canonical is a claim about WHOSE page this is, so the shop pages declare
 * todae.net even when served from the planner's origin under /todae.
 *
 * `shopUrl()` with no argument is the storefront home.
 */
export function shopUrl(subPath = ''): string {
  const clean = subPath.replace(/^\/+/, '');
  return `${ENTITIES.shop.origin}/${clean}`;
}

/**
 * Absolute URL of a planning-platform page — the deliberate crossing from shop
 * to designer, and the one direction where naming the sibling is the point
 * rather than a leak. Relative inside the planner build, where it is just a
 * route; a full origin on the shop, where it leaves the site.
 */
export function plannerUrl(subPath = ''): string {
  const clean = subPath.replace(/^\/+/, '');
  return isShop ? `${ENTITIES.planner.origin}/${clean}` : `/${clean}`;
}

/**
 * This platform's own privacy policy. They are different documents by different
 * responsible parties under POPIA: the planner's covers design projects and
 * subscriptions, the shop's covers orders, delivery addresses and payments.
 */
export const PRIVACY_HREF = isShop ? '/legal/privacy' : '/rodo';

/** The registered company operating a given platform. */
export interface Entity {
  /** Trading name shown in chrome. */
  name: string;
  /** Registered name — invoices, terms, ECTA s43(1) disclosure. */
  legalName: string;
  /** CIPC registration number (ECTA s43(1)(c)). */
  registrationNumber: string;
  /** Registered office (ECTA s43(1)(d)). */
  address: string;
  /** The address a visitor of THIS site writes to. Never the sibling's. */
  email: string;
  /** Canonical origin, no trailing slash. */
  origin: string;
}

/**
 * The two origins, overridable for local work only. Running the planner on one
 * port and the shop on another is the same shape as production — one server,
 * two domains — so `VITE_SHOP_ORIGIN=http://127.0.0.1:<port>` in .env.development.local makes
 * the planner's "Store" links and the cart handoff land on the local shop, and
 * `VITE_PLANNER_ORIGIN` does the same for the shop's "Open the designer".
 * Unset in production: the defaults are the real domains.
 */
const originOr = (value: string | undefined, fallback: string): string =>
  (value && value.trim() ? value.trim() : fallback).replace(/\/+$/, '');

export const ENTITIES: Record<Platform, Entity> = {
  planner: {
    name: 'TodAidesign',
    legalName: 'TodAidesign',
    registrationNumber: '2026/524289/07',
    address: '52 Berrydel Avenue, Somerset West, Cape Town, 7130, South Africa',
    email: 'support@todaidesign.com',
    origin: originOr(import.meta.env.VITE_PLANNER_ORIGIN, 'https://todaidesign.net'),
  },
  shop: {
    name: 'Todae',
    legalName: 'Todae (Pty) Ltd',
    registrationNumber: '2026/563609/07',
    address: '52 Berrydel Avenue, Somerset West, Cape Town, 7130, South Africa',
    email: 'info@todae.net',
    origin: originOr(import.meta.env.VITE_SHOP_ORIGIN, 'https://todae.net'),
  },
};

/**
 * The origin THIS build is served from — the base for canonicals, hreflang and
 * og:url.
 *
 * A canonical is a claim about which URL is the real one, so a shop page that
 * names the planner's origin tells Google that Todae's terms live at a
 * TodAidesign address that does not exist. The planner keeps BRAND.siteUrl,
 * which still points at the .com until the .net cutover lands.
 */
export const SITE_ORIGIN = isShop ? ENTITIES.shop.origin : BRAND.siteUrl;

/** The entity operating THIS build. */
export const ENTITY = ENTITIES[PLATFORM];

/** The other platform's public origin — for the deliberate, labelled crossing
 *  between design and purchase. Never for presenting one as the other. */
export const SIBLING_ORIGIN = ENTITIES[isShop ? 'planner' : 'shop'].origin;
